import { BankTransferType } from './beneficiaries'
import { Currencies, Error } from './types'

export const DepositAccountNumberFormats = {
  ABA: 'ABA',
  BBAN: 'BBAN',
  CUBIX: 'CUBIX',
  IBAN: 'IBAN',
  SCAN: 'SCAN',
  SWIFT: 'SWIFT',
} as const

export type DepositAccountNumberFormat =
  (typeof DepositAccountNumberFormats)[keyof typeof DepositAccountNumberFormats]

export interface BankAccountsDetails {
  isAggregatedWallet: boolean
  segregatedPaymentMethod: SegregatedPaymentMethod | null
  aggregatedPaymentMethods: AggregatedPaymentMethod[]
}

export interface AggregatedPaymentMethod {
  accountDocument: null
  currency: Currencies
  description: string
  id: number
  instantFundingOptions: unknown[]
  options: Options
  otp: null
  status: string
  type: PaymentMethodType
}

export interface BankOrBeneficiaryAddress {
  city?: string
  countryCode?: string
  // V1
  addressLine?: string
  postalCode?: string
  // V2
  addressLine1?: string
  addressLine2?: string
  postCode?: string
}

export interface Options
  extends BaseOptions,
    SwiftDetailOptions,
    EurDetailOptions,
    GbpDetailOptions,
    ZarDetailOptions,
    UsdDetailOptions,
    NgnDetailOptions,
    DepositBankAccount {}

interface BaseOptions {
  accountHolderName?: string
  bankName?: string
  reference?: string
  type?: BankTransferType
  countryCode?: string
  countryName?: string
  description?: string
}

interface SwiftDetailOptions {
  beneficiaryName?: string
  beneficiaryAddress?: BankOrBeneficiaryAddress
  bankAddress?: BankOrBeneficiaryAddress
  metaData?: {
    referencePrefix?: string
  }
}

interface EurDetailOptions {
  iban?: string
  bic?: string
}

interface GbpDetailOptions {
  ukAccountNumber?: string
  ukSortCode?: string
}

interface ZarDetailOptions {
  accountNumber?: string
  routingNumber?: string
  accountType?: string
}

interface NgnDetailOptions {
  ngnAccountNumber?: string
  bankId?: string
}

interface UsdDetailOptions {
  usdAccountNumber?: string
  cubixAccountNumberDigits?: string
  usdBankRoutingNumber?: string
  bankCity?: string
  bankCountry?: string
  bankPostalCode?: string
  bankState?: string
  referenceNumber?: string
}

export interface PaymentMethodType {
  category: Category
  countryOptions: null
  enabled: boolean
  id: number
  name: string
  options: null
}

interface Category {
  code: string
  enabled: boolean
  id: number
}

export interface SegregatedPaymentMethod {
  accountDocument: null
  currency: Currencies
  description: string
  id: number
  instantFundingOptions: unknown[]
  options: Options
  otp: null
  status: string
  type: PaymentMethodType
}

export interface BankAccountError {
  data: {
    errorList: Error[]
  }
}

export interface BusinessData {
  id: number
  entityTypeId: number
  countryCode: string
  registeredName: string
  tradingAs?: string
  businessRegistrationNumber?: string
  businessIncomeTaxNumber?: number
  operatingLicenseRequired?: boolean
  operatingLicenseType?: string
  operatingLicenseIssuer?: string
}

// Deposit bank account
export interface DepositBankAccount {
  currency?: string
  accountHolderName?: string
  accountNumber?: string
  bankCode?: string
  bankName?: string // required if SWIFT
  reference?: string
  paymentMethod?: string
  accountNumberFormat?: DepositAccountNumberFormat
  accountType?: string // required if ZAR
  isSwift?: boolean
  beneficiaryAddress?: BankOrBeneficiaryAddress
  bankAddress?: BankOrBeneficiaryAddress
  metaData?: {
    referencePrefix?: string
  }
}
